import { useFetcher, useLocation } from '@remix-run/react';
import { useEffect, useState } from 'react';
import type { StyleRule } from '@archipro-design/aria';
import { Loader, pxToRem, useStyles } from '@archipro-design/aria';
import { AllowedActions } from '@modules/design-board/actions/handler-mappings';
import * as S from '@modules/design-board/component/manage/collaborator-overview/CollaboratorsOverview.style';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import { Animation } from '@fluentui/react-northstar';
import classNames from 'classnames';

const RenderLoader = () => {
    const { css } = useStyles();

    return (
        <div className={classNames(css(loader), 'ResendInviteLink__Root')}>
            <Animation name="rotateUltraSlow">
                <Loader size="small" />
            </Animation>
        </div>
    );
};

export const ResendInviteLink = ({ email }: { email: string }) => {
    const location = useLocation();
    const [status, setStatus] = useState<
        're-sent' | 'sending' | 'pending-accept'
    >('pending-accept');
    const { css } = useStyles({ isResent: status === 're-sent' });
    const context = useDesignBoardCollaboratorsContext();
    const fetcher = useFetcher();

    const handleResendInvite = () => {
        try {
            fetcher.submit(
                {
                    email,
                    boardId: context.state.boardId.toString(),
                    _action: AllowedActions.enum.resend_invite,
                },
                {
                    method: 'post',
                    action: location.pathname,
                }
            );
        } catch {
            setStatus('pending-accept');
        }
    };

    useEffect(() => {
        switch (fetcher.state) {
            case 'submitting':
                setStatus('sending');
                break;
            case 'loading':
                setStatus('re-sent');
                break;
        }
    }, [email, fetcher]);

    switch (status) {
        case 'pending-accept':
            return (
                <button
                    className={css(S.ResendButton)}
                    type={'button'}
                    onClick={handleResendInvite}
                >
                    Resend Invite
                </button>
            );
        case 'sending':
            return <RenderLoader />;
        case 're-sent':
            return (
                <button
                    className={css(S.ResendButton)}
                    type={'button'}
                    disabled={true}
                >
                    Invite Sent
                </button>
            );
        default:
            return null;
    }
};

const loader: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.primitive.white,
    '& svg': {
        fill: theme.siteVariables.colors.primitive.white,
    },
    '& span': {
        width: pxToRem(107),
        height: pxToRem(107),
    },
});
